import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();

  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!user?.id) return;

      try {
        const { data: userListings } = await supabase
          .from('listings')
          .select('id')
          .eq('host_id', user.id);

        const userListingIds = userListings?.map(l => l.id) || [];

        const { data: recentBookings } = await supabase
          .from('bookings')
          .select('*')
          .in('listing_id', userListingIds)
          .order('created_at', { ascending: false })
          .limit(10);

        const renterIds = [...new Set(recentBookings?.map(b => b.renter_id) || [])];
        const { data: renters } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', renterIds);

        const listingIds = [...new Set(recentBookings?.map(b => b.listing_id) || [])];
        const { data: listings } = await supabase
          .from('listings')
          .select('id, title, price_per_month')
          .in('id', listingIds);

        const activities = (recentBookings || []).map(booking => {
          const renter = renters?.find(r => r.id === booking.renter_id);
          const listing = listings?.find(l => l.id === booking.listing_id);

          const renterName = renter?.full_name?.split(' ')[0]
            || renter?.email?.split('@')[0]
            || 'Someone';

          const listingTitle = listing?.title || 'your space';
          const amount = booking.total_amount || 0;
          const months = booking.months || 1;

          return {
            id: booking.id,
            type: 'booking',
            icon: 'ri-check-line',
            iconColor: 'bg-green-100 text-green-600',
            title: 'New booking received',
            description: `${renterName} booked ${listingTitle} for ${months} ${months === 1 ? 'month' : 'months'}`,
            timestamp: booking.created_at,
            amount,
          };
        });

        setRecentActivity(activities);
      } catch (_error: unknown) {
        // silently handle error
      } finally {
        setLoadingActivity(false);
      }
    };

    fetchRecentActivity();
  }, [user?.id]);

  // Use profile data from Supabase
  const userName = profile?.full_name || user?.user_metadata?.full_name || 'User';
  const userEmail = profile?.email || user?.email || 'user@email.com';
  const isVerified = profile?.is_verified || false;
  
  // Format member since date
  const memberSince = profile?.created_at 
    ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (_error: unknown) {
      // silently handle error
    }
  };

  const dashboardSections = [
    {
      title: 'My Account',
      description: 'View and edit your personal information',
      icon: 'ri-user-line',
      color: 'from-[#14B8A6] to-teal-600',
      path: '/settings',
    },
    {
      title: 'My Listings',
      description: 'Manage your storage spaces',
      icon: 'ri-home-4-line',
      color: 'from-blue-500 to-blue-600',
      path: '/my-listings',
    },
    {
      title: 'My Bookings',
      description: 'View your storage bookings',
      icon: 'ri-calendar-check-line',
      color: 'from-green-500 to-green-600',
      path: '/bookings',
    },
    {
      title: 'Messages',
      description: 'Chat with hosts and renters',
      icon: 'ri-message-3-line',
      color: 'from-orange-500 to-orange-600',
      path: '/messages',
    },
    {
      title: 'Rental History',
      description: 'View your past rentals',
      icon: 'ri-history-line',
      color: 'from-purple-500 to-purple-600',
      path: '/rental-history',
    },
    {
      title: 'Earnings',
      description: 'Track your hosting income',
      icon: 'ri-money-dollar-circle-line',
      color: 'from-yellow-500 to-yellow-600',
      path: '/earnings',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Dashboard</h1>
                <p className="text-gray-600 mt-2">Welcome back, {userName.split(' ')[0]}!</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line text-lg"></i>
                Log Out
              </button>
            </div>
          </div>

          {/* Profile Card */}
          <div className="bg-gradient-to-r from-[#14B8A6] to-blue-600 rounded-xl shadow-lg p-8 mb-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-2xl font-bold">
                {userName.split(' ').map(n => n[0]).join('').toUpperCase()}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold">{userName}</h2>
                <p className="text-teal-50 mt-1">{userEmail}</p>
                <div className="flex items-center gap-4 mt-3">
                  {isVerified ? (
                    <div className="flex items-center gap-2 text-sm">
                      <i className="ri-shield-check-line"></i>
                      <span>Verified Account</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm opacity-60">
                      <i className="ri-shield-line"></i>
                      <span>Unverified Account</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-calendar-line"></i>
                    <span>Member since {memberSince}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Sections */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Access</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboardSections.map((section, index) => (
              <div
                key={index}
                onClick={() => navigate(section.path)}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className={`h-2 bg-gradient-to-r ${section.color}`}></div>
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${section.color} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${section.icon} text-white text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#14B8A6] transition-colors">
                        {section.title}
                      </h4>
                      <p className="text-sm text-gray-600">{section.description}</p>
                    </div>
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-[#14B8A6] transition-colors"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Activity */}
          <div className="mt-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h3>
            {loadingActivity ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <i className="ri-loader-4-line text-2xl text-gray-400 animate-spin"></i>
                <p className="mt-2 text-gray-600">Loading activity...</p>
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <i className="ri-inbox-line text-4xl text-gray-300 mb-2"></i>
                <p className="text-gray-600">No recent activity</p>
                <p className="text-sm text-gray-500 mt-1">Your bookings will appear here</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                    <div className={`w-10 h-10 rounded-full ${activity.iconColor} flex items-center justify-center flex-shrink-0`}>
                      <i className={activity.icon}></i>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{activity.title}</p>
                      <p className="text-sm text-gray-600">{activity.description}</p>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(activity.timestamp).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
